// Owner: shell-nav. Workspaces of the signed-in person, switching between them and the member's
// language: shared by the rail, the mobile menu and the command bar.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { isApiError } from '@dataroom/api-client';
import { accessApi } from '@dataroom/api-client/access';
import { z } from 'zod';
import { useToast } from '@dataroom/ui';
import { api } from '../lib/api';
import { applyTheme } from '../features/account/theme';
import { updateMyPreferences } from '../lib/auth';
import { applyFormats, t } from '../lib/i18n';
import { qk, useMe } from '../lib/queries';

/** What a refused switch says when that workspace needs its own sign-in first. */
const SignInRequired = z.object({ reason: z.literal('sign_in_required'), slug: z.string().min(1) });

const accessClient = accessApi(api.http);

export function workspacesQuery(workspaceId: string) {
  return {
    queryKey: ['me', 'workspaces', workspaceId] as const,
    queryFn: () => accessClient.workspaces.list(),
    staleTime: 60_000,
  };
}

/** The other workspaces of the person and a switch action. */
export function useWorkspaces() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const toast = useToast();
  const list = useQuery(workspacesQuery(me.workspace.id));
  const switchTo = useMutation({
    mutationFn: (id: string) => accessClient.workspaces.switch(id),
    onSuccess: async (next) => {
      // Drop every cached answer of the previous workspace except the member itself: a mounted
      // observer of `me` must keep its query, or the shell would never see the new workspace. The
      // layout remounts the whole screen on the new workspace id (routes/_app.tsx).
      qc.removeQueries({ predicate: (q) => !(q.queryKey.length === 1 && q.queryKey[0] === qk.me[0]) });
      qc.setQueryData(qk.me, next);
      applyTheme(next.preferences.theme ?? 'system');
      // Another workspace can speak another language: strings are not reactive, so reload (as a
      // change of language in the account area does).
      if (applyFormats(next.formats)) {
        window.location.assign('/');
        return;
      }
      await router.invalidate();
      await navigate({ to: '/' });
    },
    onError: (err) => {
      const required = SignInRequired.safeParse(isApiError(err) ? err.details : undefined);
      if (required.success) {
        void navigate({ to: '/login', search: { workspace: required.data.slug } });
        return;
      }
      toast({
        title: t('shell.switchFailed'),
        body: err instanceof Error ? err.message : undefined,
        tone: 'err',
      });
    },
  });
  return {
    me,
    all: list.data ?? [],
    others: (list.data ?? []).filter((w) => !w.current),
    /** Switch, then land on Home; `onSwitched` runs once the new workspace is in (close a sheet). */
    switchTo: (id: string, onSwitched?: () => void) =>
      switchTo.mutate(id, onSwitched ? { onSuccess: () => onSwitched() } : undefined),
    switching: switchTo.isPending,
  };
}

export type LanguageChoice = 'default' | 'en' | 'de';

/** The member's language choice (the workspace default unless they chose one) and a setter. */
export function useLanguage() {
  const me = useMe();
  const current: LanguageChoice = me.preferences.locale ?? 'default';
  const workspaceLanguage = t(me.workspace.locale === 'de' ? 'shell.language.de' : 'shell.language.en');
  return {
    current,
    options: [
      { value: 'default' as const, label: t('shell.language.default', { language: workspaceLanguage }) },
      { value: 'en' as const, label: t('shell.language.en') },
      { value: 'de' as const, label: t('shell.language.de') },
    ],
    set: (value: LanguageChoice) => void updateMyPreferences({ locale: value === 'default' ? null : value }),
  };
}
